import React, { useEffect, useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, TextField, Button, Alert, Divider } from '@mui/material';
import API_URL from '../config/api';

const fileNames = [
  { key: 'rules', label: 'Regras (rules.v1.json)', type: 'json' },
  { key: 'guardrails', label: 'Guardrails (guardrails.v1.json)', type: 'json' },
  { key: 'prompts', label: 'Prompts (prompts.v1.md)', type: 'md' },
];

const InsightsConfigEditor = () => {
  const [configInfo, setConfigInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState({ rules: '', guardrails: '', prompts: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadInfo();
    // Load initial files
    fileNames.forEach((f) => loadFile(f.key));
  }, []);

  const pushMsg = (type, text) => setMessages((prev) => [...prev, { type, text }]);

  const loadInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/insights/config`, { credentials: 'include' });
      const json = await res.json();
      if (json.success) setConfigInfo(json.data);
    } catch (e) {
      pushMsg('error', 'Falha ao carregar informações: ' + e.message);
    }
  };

  const loadFile = async (name) => {
    try {
      const res = await fetch(`${API_URL}/api/insights/file?name=${name}`, { credentials: 'include' });
      const json = await res.json();
      if (json.success) {
        setFiles((prev) => ({ ...prev, [name]: json.data.content }));
      } else {
        pushMsg('error', `Falha ao carregar ${name}: ${json.message}`);
      }
    } catch (e) {
      pushMsg('error', `Erro ao carregar ${name}: ${e.message}`);
    }
  };

  const saveFile = async (name) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/insights/file`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, content: files[name] }),
      });
      const json = await res.json();
      if (json.success) pushMsg('success', `${name}: salvo com sucesso`);
      else pushMsg('error', `${name}: ${json.message}`);
    } catch (e) {
      pushMsg('error', `${name}: erro ao salvar - ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5">Configuração do Consultor Virtual</Typography>
        <Typography variant="body2" color="text.secondary">
          Edite regras, guardrails e prompts. As alterações são validadas no backend e aplicadas imediatamente.
        </Typography>
      </Box>

      {messages.map((m, i) => (
        <Alert key={i} severity={m.type} onClose={() => setMessages((prev) => prev.filter((_, idx) => idx !== i))} sx={{ mb: 1 }}>
          {m.text}
        </Alert>
      ))}

      {configInfo && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Indicadores: {configInfo.counts.indicators} • Regras: {configInfo.counts.rules}
        </Alert>
      )}

      <Grid container spacing={2}>
        {fileNames.map((f) => (
          <Grid item xs={12} md={f.key === 'prompts' ? 12 : 6} key={f.key}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {f.label}
                </Typography>
                <TextField
                  value={files[f.key]}
                  onChange={(e) => setFiles((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={f.key === 'prompts' ? 12 : 14}
                  placeholder={f.type === 'json' ? '{ ... }' : '# ...'}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button variant="contained" onClick={() => saveFile(f.key)} disabled={loading}>
                    Salvar
                  </Button>
                  <Button variant="outlined" onClick={() => loadFile(f.key)} disabled={loading}>
                    Recarregar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Dica: mantenha o `rules.v1.json` simples e use tags para organizar insights por tema. Guarde referências no docs/ai/CONSULTOR_PLAYBOOK.md.
      </Typography>
    </Box>
  );
};

export default InsightsConfigEditor;

